/**
 * Roles, scope and capabilities - the single source of truth, imported by the
 * API middleware and by the console's route guards so the two cannot drift.
 *
 * **Every user has a mandatory home company and site**, superadmin included: the
 * home is where sign-in lands them and what every dashboard defaults to. How far
 * past that home they may look - their *reach* - comes from the role, and for a
 * field officer from rows in `site_memberships`:
 *
 *   superadmin   every company, every site        (Visionify staff)
 *   admin        every site under their company    (customer EHS / ops leadership)
 *   fieldofficer the sites they are a member of    (the technician)
 *
 * Home and reach are separate ideas. A superadmin's home is a real site they
 * land on; their *reach* is unbounded. Do not conflate the two - `scopeFor()`
 * returns the reach, and `auth.companyId` / `auth.siteId` carry the home.
 *
 * ── Ask about the reach, never about the role ───────────────────────────────
 * `isUnbounded()`, `boundedCompanyId()` and `boundedSiteIds()` exist so that no
 * caller anywhere has to write `role === "superadmin"`. Every such comparison is
 * a place that silently breaks the day a fourth role appears, and there is no
 * question about *access* that the scope cannot answer. If you find yourself
 * needing the role name for anything other than a label, the missing piece
 * belongs here.
 *
 * Enforced twice, on purpose. Postgres RLS is the real boundary - see the
 * `app.*` helpers in the first migration - and the API applies the same rule as
 * a query filter so list endpoints return the right rows rather than relying on
 * RLS to silently drop them. If the two ever disagree, RLS wins and the bug
 * shows up as an empty list, which is the safe direction to fail.
 *
 * ── Planned evolution ───────────────────────────────────────────────────────
 * The capability sets below are compiled in. A per-role `permissions` table
 * (route × role → 0 disabled / 1 read / 2 write / 3 delete), editable by a
 * superadmin, is planned so customers can configure access themselves - see
 * docs/phases/phase-6-admin-privacy.md and docs/04-rbac.md. Call sites gate on
 * capabilities rather than role names precisely so that swap does not touch them.
 */

import { titleCase } from "./format.js";

/* ────────────────────────────── Roles ────────────────────────────── */

export const ROLES = ["superadmin", "admin", "fieldofficer"] as const;

export type Role = (typeof ROLES)[number];

export function isRole(value: unknown): value is Role {
  return typeof value === "string" && (ROLES as readonly string[]).includes(value);
}

/** Human labels, title-cased via `titleCase`. The console renders these; never render the raw slug. */
export const ROLE_LABELS: Record<Role, string> = {
  superadmin: titleCase("super admin"),
  admin: titleCase("admin"),
  fieldofficer: titleCase("field officer"),
};

export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  superadmin: "Access to every workspace and every location, from a home location of their own.",
  admin: "Full access to every location within their own workspace.",
  fieldofficer: "Access limited to the locations they are assigned. The technician in the field.",
};

/**
 * How the console explains a role's reach when nothing tenant-specific is to
 * hand. Where a company or site name *is* available, prefer naming it - "Every
 * site in Acme Utilities" beats "Every site in their company".
 */
export const ROLE_REACH_LABELS: Record<Role, string> = {
  superadmin: "Every workspace and location",
  admin: "Every location in their workspace",
  fieldofficer: "Assigned locations only",
};

/**
 * Whether `site_memberships` decides what this role can reach.
 *
 * False for a superadmin (unbounded) and for an admin (bounded by company, not
 * by rows). Their memberships are still maintained - a demotion must not strand
 * anyone - but granting one changes nothing about what they can see, and the
 * console says so rather than implying a checkbox has an effect it does not.
 */
export function reachIsMembershipBased(role: Role): boolean {
  return role === "fieldofficer";
}

/**
 * Whether this role has `site_memberships` rows at all.
 *
 * A superadmin has none by design, and the database refuses to create one - see
 * `app.enforce_site_membership_rules`. The console shows them a "reaches every
 * site" note instead of an empty picker.
 */
export function hasSiteMemberships(role: Role): boolean {
  return role !== "superadmin";
}

/**
 * A Visionify staff role rather than a customer one.
 *
 * Exists so that "highlight this person, they are not one of your colleagues" is
 * a named question rather than an inline `role === "superadmin"` in a component.
 * It is presentation only - nothing about access should be decided from it; ask
 * the scope for that.
 */
export function isPlatformRole(role: Role): boolean {
  return role === "superadmin";
}

/**
 * The role that does the work in the field.
 *
 * Presentation only, exactly like `isPlatformRole` above - "which of these
 * people are the technicians" is a job description, not an access decision, and
 * nothing about what somebody may see or do should be decided from it. It exists
 * so that picking the crew out of a site roster is a named question here rather
 * than a `role === "fieldofficer"` in a component.
 */
export function isFieldRole(role: Role): boolean {
  return role === "fieldofficer";
}

/**
 * Ordering, used only for "can this person manage that person" questions.
 * Higher wins. Not a permission check - capabilities are.
 */
export const ROLE_RANK: Record<Role, number> = {
  superadmin: 100,
  admin: 50,
  fieldofficer: 10,
};

/**
 * You may only assign a role at or below your own rank, and never above it.
 * An admin creating another admin is fine; an admin minting a superadmin is not.
 * Mirrored by `app.can_assign_role` so it holds even if a caller reaches the
 * table another way.
 *
 * Written against `ROLE_RANK` rather than by naming the roles. The name version
 * was three literals that a fourth role would have quietly excluded from
 * everything, and the SQL side has always been the rank comparison - two
 * implementations of one rule is how they drift.
 */
export function canAssignRole(actor: Role, target: Role): boolean {
  // Both halves are load-bearing. The rank alone would let a field officer
  // assign another field officer - 10 <= 10 - which is the one case the name
  // version got right by accident and `app.can_assign_role` got wrong until
  // this pair was written down. Provisioning is an administrative act.
  return roleAdministers(actor) && ROLE_RANK[target] <= ROLE_RANK[actor];
}

/**
 * Does this role administer the work rather than carry it out?
 *
 * Mirrors `app.role_administers`. The one place the two administrative role
 * names are listed on this side, so a fourth role is a single edit rather than
 * a hunt through predicates that each spelled the list out again.
 */
export function roleAdministers(role: Role): boolean {
  return role === "superadmin" || role === "admin";
}

/* ────────────────────────────── Scope ────────────────────────────── */

export type ScopeKind = "global" | "company" | "sites";

/**
 * The resolved visibility window for one request - the *reach*, not the home.
 *
 * `companyId` and `siteIds` are the **bounds**, and `null` on either means
 * "unbounded on this axis" rather than "none". That inversion is the one trap
 * here: treating a null `companyId` as no-access hands a superadmin an empty
 * console. Branch on `kind`, or better, use the three accessors below.
 *
 * The caller's home company and site are always populated on `req.auth`
 * regardless of reach, so a screen always has somewhere to default to.
 */
export interface Scope {
  kind: ScopeKind;
  /** Bound company, or null when unbounded. */
  companyId: string | null;
  /** Bound sites, or null when unbounded by site. Never an empty-array stand-in for null. */
  siteIds: readonly string[] | null;
}

/**
 * @param siteIds every site the user is a member of. Always contains their home
 * site - the database guarantees it via `app.sync_home_site_membership`, so
 * nothing here has to add it back. Empty for a superadmin, and ignored for them.
 */
export function scopeFor(role: Role, companyId: string, siteIds: readonly string[] = []): Scope {
  switch (role) {
    case "superadmin":
      // Has a home company and site, but is bounded by neither.
      return { kind: "global", companyId: null, siteIds: null };
    case "admin":
      // Bounded by company, not by memberships. An admin reaches every site
      // under their company whether or not a row says so.
      return { kind: "company", companyId, siteIds: null };
    case "fieldofficer":
      return { kind: "sites", companyId, siteIds: [...siteIds] };
  }
}

/* ─── The three questions. Ask these, never `role === "superadmin"`. ─── */

/** True when nothing bounds this caller - the only honest name for it. */
export function isUnbounded(scope: Scope): boolean {
  return scope.kind === "global";
}

/** The company this caller is pinned to, or null when they may cross companies. */
export function boundedCompanyId(scope: Scope): string | null {
  return scope.companyId;
}

/** The sites this caller is pinned to, or null when they are not bounded by site. */
export function boundedSiteIds(scope: Scope): readonly string[] | null {
  return scope.siteIds;
}

/** True when `scope` permits reading rows belonging to `companyId`. */
export function scopeAllowsCompany(scope: Scope, companyId: string): boolean {
  return scope.companyId === null || scope.companyId === companyId;
}

/**
 * True when `scope` permits reading rows belonging to `siteId`.
 *
 * A company-scoped caller cannot answer this from the scope alone - it needs to
 * know which company the site belongs to - so callers pass that in. Requiring
 * the argument is the point: it makes the missing lookup a type error rather
 * than a silent "true".
 */
export function scopeAllowsSite(scope: Scope, siteId: string, siteCompanyId: string): boolean {
  if (!scopeAllowsCompany(scope, siteCompanyId)) return false;
  return scope.siteIds === null || scope.siteIds.includes(siteId);
}

/* ─────────────────────────── Capabilities ─────────────────────────── */

/**
 * What a role may *do*, as opposed to what it may *see*. Scope answers "which
 * rows"; capability answers "which verbs". Both are checked - a field officer
 * has `assessment.create` but only within their own site.
 *
 * Capabilities beyond the current phase are listed already so the console and
 * the mobile app can gate their navigation from day one instead of hard-coding
 * role names in twenty components.
 */
export const CAPABILITIES = [
  "company.read",
  "company.write",
  "site.read",
  "site.write",
  "user.read",
  "user.invite",
  "user.write",
  "appointment.read",
  "appointment.write",
  "visit.read",
  "visit.write",
  "assessment.read",
  "assessment.create",
  "hazard.acknowledge",
  "stopwork.raise",
  "stopwork.resolve",
  "report.export",
  "settings.manage",
  "platform.admin",
] as const;

export type Capability = (typeof CAPABILITIES)[number];

const ROLE_CAPABILITIES: Record<Role, readonly Capability[]> = {
  superadmin: CAPABILITIES,

  admin: [
    "company.read",
    "site.read",
    "site.write",
    "user.read",
    "user.invite",
    "user.write",
    "appointment.read",
    "appointment.write",
    "visit.read",
    "visit.write",
    "assessment.read",
    "assessment.create",
    "hazard.acknowledge",
    "stopwork.raise",
    "stopwork.resolve",
    "report.export",
    "settings.manage",
  ],

  // Narrow at the ADMINISTRATIVE line, not at the line between planning work and
  // doing it. Those are different cuts and the second one was the wrong one: a
  // technician who arrives at a job nobody dispatched could open a visit and had
  // no way to create the appointment it hangs off, so the role that runs every
  // assessment could not record that the work happened.
  //
  // What they hold is the job: plan it, change it, do it, record it, and push
  // back on it. They can raise a stop-work, because the app is the technician's
  // tool for refusing an unsafe job - resolving one is a supervisor's decision
  // and stays above.
  //
  // What they do not hold is administration: inviting or editing people
  // (`user.invite`, `user.write`), opening and closing sites (`site.write`),
  // company settings, and exports, which are a compliance function.
  //
  // `user.read` without `user.write` is the deliberate pair. It is what fills
  // the assignee picker on the appointment form and the technician's name on a
  // visit record - both of which read people they already work beside - while
  // every change to an account stays administrative. The matching RLS widening
  // is in ..._field_officers_schedule.sql and is scoped to shared sites.
  //
  // `appointment.read` is not narrowed to their own rows: a technician needs to
  // see who else is at the site today, and the RLS policy gives them the whole
  // site rather than a disjunction someone eventually gets wrong.
  fieldofficer: [
    "site.read",
    "user.read",
    "appointment.read",
    "appointment.write",
    "visit.read",
    "visit.write",
    "assessment.read",
    "assessment.create",
    "hazard.acknowledge",
    "stopwork.raise",
  ],
};

export function capabilitiesFor(role: Role): readonly Capability[] {
  return ROLE_CAPABILITIES[role];
}

export function hasCapability(role: Role, capability: Capability): boolean {
  return ROLE_CAPABILITIES[role].includes(capability);
}

/* ─────────────────── Access levels (planned, Phase 6) ─────────────────── */

/**
 * Graded access for the per-role `permissions` table planned in
 * docs/phases/phase-6-admin-privacy.md. Defined here now so the API, the
 * console and the migration all agree on the encoding when it lands.
 *
 * Until then, roles and their capabilities are **constant and seeded** - the
 * three roles are database rows, and what each may do is the compiled-in map
 * above. Phase 6 turns that into a grid a superadmin edits: routes down one
 * axis, roles across the other, one of these four values in each cell, plus the
 * ability to add a route to the catalog.
 *
 * Ordinal comparison is the whole check: `level >= required`. WRITE implies
 * READ, DELETE implies WRITE. Absent row resolves to DISABLED - deny by default.
 */
export const ACCESS = {
  DISABLED: 0,
  READ: 1,
  WRITE: 2,
  DELETE: 3,
} as const;

export type AccessLevel = (typeof ACCESS)[keyof typeof ACCESS];

export const ACCESS_LABELS: Record<AccessLevel, string> = {
  0: "Disabled",
  1: "Read",
  2: "Read and write",
  3: "Read, write and delete",
};
